import React, { useState, useRef, useMemo } from 'react';
import { Modal } from '@johnlewispartnership/wtr-ingredients/ingredients/Modal';
import classNames from 'classnames';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import {
  Accordion,
  AccordionItem,
} from '@johnlewispartnership/wtr-ingredients/ingredients/Accordion';
import {
  ChevronDown,
  RemoveOutline,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { CloseButton } from '@johnlewispartnership/wtr-ingredients/ingredients/Modal/CloseButton';
import { Button } from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { Checkbox } from '@johnlewispartnership/wtr-ingredients/ingredients/Checkbox';
import { Radio } from '@johnlewispartnership/wtr-ingredients/ingredients/Radio/Radio';
import { LabeledControl } from '@johnlewispartnership/wtr-ingredients/ingredients/LabeledControl';

import { recipeFilterModal } from 'constants/modals';
import { trackModalClosed, trackModalOpened } from 'analytics/modal-tracking';

import styles from './index.module.scss';

export interface FilterValue {
  name: string;
  count: number;
}

export interface Filter {
  title: string;
  values: FilterValue[];
}

export type CheckedState = boolean[][];

interface FiltersProps {
  filters?: Filter[];
  externalState?: CheckedState | null;
  showApplied?: boolean;
  enableMobile?: boolean;
  closedItems?: string[];
  showSectionItemsAsRadio?: string[];
  onChange?: (checkedState: CheckedState, filters: Filter[]) => void;
  onClose?: (buttonRef: React.RefObject<HTMLButtonElement | null>) => void;
  isFiltersModalOpen?: boolean;
  toggleModal?: () => void;
}

const filtersDefaultPropFilters: Filter[] = [];
const filtersDefaultPropClosedItems: string[] = [];
const filtersDefaultPropShowSectionItemsAsRadio: string[] = [];
const filtersDefaultPropOnChange = (): void => {};
const filtersDefaultPropOnClose = (): void => {};

const Filters: React.FC<FiltersProps> = ({
  filters = filtersDefaultPropFilters,
  externalState = null,
  showApplied = true,
  enableMobile = true,
  closedItems = filtersDefaultPropClosedItems,
  showSectionItemsAsRadio = filtersDefaultPropShowSectionItemsAsRadio,
  onChange = filtersDefaultPropOnChange,
  onClose = filtersDefaultPropOnClose,
  isFiltersModalOpen = false,
  toggleModal,
}) => {
  const emptyState = (): CheckedState => Array.from({ length: filters.length }, () => []);

  const [checkedState, setChecked] = useState<CheckedState>(emptyState());
  const [expanded, setExpanded] = useState<boolean[]>(
    Array.from({ length: filters.length }, () => false),
  );

  const checked = externalState || checkedState;

  const hasSelection = useMemo(
    () => !!checked.reduce((acc, item) => (acc || item.filter(v => v).length) as boolean, false),
    [checked],
  );

  const buttonRef = useRef<HTMLButtonElement>(null);

  const { id, title: titleText, severity } = recipeFilterModal;

  const filterModal = {
    id,
    title: titleText,
    severity,
  };

  const openModal = () => {
    setTimeout(() => {
      if (toggleModal) toggleModal();
      trackModalOpened(filterModal);
    }, 0);
  };

  const closeModal = () => {
    setTimeout(() => {
      if (toggleModal) toggleModal();
      buttonRef.current?.focus();
      onClose(buttonRef as React.RefObject<HTMLButtonElement>);
      trackModalClosed(filterModal);
    }, 0);
  };

  const clear = () => {
    const checkedIsNotEmpty = checked.some(item => item.length);
    closeModal();
    if (!checkedIsNotEmpty) return;
    const state = emptyState();
    setChecked(state);
    onChange(state, filters);
  };

  const update = () => {
    onChange(checked, filters);
  };

  const done = () => {
    closeModal();
    trackModalClosed(filterModal);
  };

  const inner = () => {
    const applied = filters.flatMap(({ values }, i) => values.filter((_, j) => checked[i][j]));

    return (
      <section
        aria-label="Recipe filters"
        className={classNames(styles.filters, {
          [styles.overlay]: isFiltersModalOpen,
        })}
      >
        <header
          className={classNames(styles.white, {
            [styles.desktop]: isFiltersModalOpen,
            hidden: hasSelection || isFiltersModalOpen,
          })}
        >
          <Typography
            element="h2"
            styleAs={isFiltersModalOpen ? 'paragraphHeading' : 'sectionHeadingSmall'}
            textStyle={isFiltersModalOpen ? 'heading-l' : 'heading-m'}
          >
            Filter {isFiltersModalOpen ? 'by' : ''}
          </Typography>
          {isFiltersModalOpen && <CloseButton close={done} className="" theme="light" />}
        </header>
        {enableMobile && (
          <div className={classNames(styles.mobile, styles.white)}>
            <Button
              label={`Filter by${applied.length ? ` (${applied.length})` : ''}`}
              theme="secondary"
              endIcon={<ChevronDown className={styles.chevronIcon} size="small" />}
              ref={buttonRef}
              className={classNames(styles.filterButton)}
              onClick={openModal}
            />
          </div>
        )}
        <div
          className={classNames(
            { [styles.inner]: enableMobile },
            styles.white,
            isFiltersModalOpen && styles.innerOpen,
          )}
        >
          {showApplied && hasSelection && (
            <div className={classNames(styles.appliedHeader, { hidden: isFiltersModalOpen })}>
              <Typography element="h3" styleAs="sectionHeadingSmall" textStyle="heading-m">
                Applied Filters
              </Typography>
              <ul>
                {filters.map(({ values }, i) =>
                  values.map(
                    (f, j) =>
                      checked[i][j] && (
                        <li className={styles.applied} key={f.name}>
                          <Typography styleAs="paragraph" textStyle="body-m">
                            {f.name}
                          </Typography>
                          <button
                            type="button"
                            aria-label={`Remove ${f.name} from applied filters`}
                            onClick={() => {
                              checked[i][j] = false;
                              if (isFiltersModalOpen || !externalState) setChecked([...checked]);
                              update();
                            }}
                          >
                            <RemoveOutline size="xsmall" className={styles.removeIcon} />
                          </button>
                        </li>
                      ),
                  ),
                )}

                <button aria-hidden="true" className={styles.clear} type="button" onClick={clear}>
                  <Typography styleAs="paragraphHeading" textStyle="heading-l">
                    Clear all
                  </Typography>
                </button>
              </ul>
            </div>
          )}

          <Accordion theme="plusMinus" scrollToItemWhenOpened={false}>
            {filters.map(({ title, values }, i) => {
              const isExpanded = expanded[i];
              const handleFilterChange = (j: number, isChecked: boolean) => {
                checked[i].length = values.length;
                checked[i][j] = isChecked;
                if (isFiltersModalOpen || !externalState) setChecked([...checked]);
                update();
              };

              return (
                <div
                  key={title}
                  className={classNames(
                    styles.accordion,
                    i === filters.length - 1 ? '' : styles.divider,
                  )}
                >
                  <AccordionItem
                    title={title}
                    headingElement="h3"
                    id={`filters-accordion-${i}`}
                    defaultExpanded={!closedItems.includes(title)}
                  >
                    <ul>
                      {values
                        .filter((_, k) => k < (isExpanded ? 1000 : 10))
                        .map((f, j) => (
                          <li key={f.name}>
                            <LabeledControl
                              control={
                                showSectionItemsAsRadio.includes(title) ? (
                                  <Radio
                                    name={`filters-${i}`}
                                    value={f.name}
                                    checked={!!checked[i][j]}
                                    onChange={() => {
                                      checked[i] = Array(values.length).fill(false);
                                      handleFilterChange(j, true);
                                    }}
                                  />
                                ) : (
                                  <Checkbox
                                    name={`filters-${i}-${j}`}
                                    value={checked[i][j] ? 'checked' : ''}
                                    checked={!!checked[i][j]}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                      handleFilterChange(j, e.target.checked)
                                    }
                                  />
                                )
                              }
                              label={
                                <>
                                  <Typography
                                    noMargins
                                    styleAs={checked[i][j] ? 'paragraphHeading' : 'paragraph'}
                                    textStyle={checked[i][j] ? 'heading-l' : 'body-m'}
                                  >
                                    {f.name}
                                  </Typography>
                                  &nbsp;
                                  <Typography noMargins styleAs="paragraph" textStyle="body-m">
                                    {(!showSectionItemsAsRadio.includes(title) || checked[i][j]) &&
                                      `(${f.count})`}
                                  </Typography>
                                </>
                              }
                            />
                          </li>
                        ))}
                    </ul>
                    {values.length > 10 && (
                      <button
                        className={styles.showMore}
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          expanded[i] = !isExpanded;
                          setExpanded([...expanded]);
                        }}
                      >
                        Show {isExpanded ? 'less' : `${values.length - 10} more`}
                      </button>
                    )}
                  </AccordionItem>
                </div>
              );
            })}
          </Accordion>

          {isFiltersModalOpen && (
            <div className={classNames(styles.hidden, { [styles.mobileBtns]: enableMobile })}>
              <Button aria-hidden="true" label="Clear all" theme="secondary" onClick={clear} />
              <Button aria-hidden="true" label="Done" theme="primary" onClick={done} />
            </div>
          )}
        </div>
      </section>
    );
  };

  return (
    <>
      {isFiltersModalOpen ? (
        <Modal
          className={styles.modalOverlay}
          contentClassName={styles.content}
          isOpen={isFiltersModalOpen}
          handleModalClose={done}
          bodyOpenClassName="mobileFiltersModal ReactModal__Body--open"
          ariaHideApp={false}
          titleText="Filter by"
          fullScreen
        >
          {inner()}
        </Modal>
      ) : (
        inner()
      )}
    </>
  );
};

export default Filters;
